import { Button, Grid, SvgIcon, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ScreeningForm from 'src/components/ScreeningForm';
import loader from 'src/components/LoaderImg/loader.gif';
import { Link } from 'react-router-dom';
import {
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { getSinglePatient } from 'src/redux/actions/patientAction';
import { getTherapistCred } from 'src/utils/get-therapist-credential';
import { getSelectedFacilityName } from 'src/utils/get-selected-facility-name';
import useCurrentUser from 'src/hooks/use-current-user';

function ViewAddNewScreenForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const { document } = location?.state || { document: '' };
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const [credentials, currentRoles, licenseNumber, discipline] = useCurrentUser([
    'credentials',
    'currentRoles',
    'licenseNumber',
    'discipline',
  ]);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const patientFetchFailed = useSelector((state: any) => state?.getSinglePatientReducer?.isError);
  const getCredentials = typeof credentials === 'string' ? credentials?.replaceAll(' ', '') : '';
  const allFacilities = useSelector(
    (state: any) => state?.getFacilityDropDownReducer?.response?.data?.data
  );
  const siteOfService = getSelectedFacilityName(allFacilities).replace(/%20/g, ' ');

  const backToEOCDetails = () => {
    navigate('/patients/patient-details/screening');
    dispatch(resetUuidAction());
    dispatch(resetForLoaderDocumentation());
  };

  // `discipline` from useCurrentUser is a wide union; the screening form only cares about the
  // string discipline code.
  const disciplineString = typeof discipline === 'string' ? discipline : '';

  // Prefill for the Reform screening form, keyed by section id to match the schema shape.
  const screeningPrefill = {
    basicDetails: {
      patientName: [patientData?.firstName, patientData?.lastName].filter(Boolean).join(' '),
      licenseNumber: licenseNumber || '',
      credentials: getCredentials || getTherapistCred(discipline, currentRoles) || '',
      siteOfService,
      ...(['OT', 'PT', 'SLP'].includes(disciplineString)
        ? { disciplineInitiatingScreen: disciplineString }
        : {}),
    },
  };

  const backToScreeningList = () => {
    // Clear the minted webhook uuid so the next screening create starts fresh.
    dispatch(resetUuidAction());
    dispatch(resetForLoaderDocumentation());
    navigate('/patients/patient-details/screening');
  };

  // The patient's name lives on the single-patient record, keyed by the (persisted) patient id —
  // the same source the patient-details header and the other documents read. This screen is its own
  // route, so on a reload or a deep-link nothing else has fetched it: ask for it here rather than
  // inheriting whatever a previously-visited page happened to leave in the store.
  useEffect(() => {
    if (!getPatientId) return;
    dispatch(getSinglePatient({ patientId: getPatientId }));
  }, [dispatch, getPatientId]);

  // Reform seeds its form data from `initialData` once, at mount, so the prefill has to be complete
  // before ScreeningForm renders — a name that arrives afterwards never reaches the field. Compare
  // the uuid rather than just checking for a patient, so a record left behind for a *different*
  // patient doesn't prefill the wrong name. A failed fetch still opens the form, name blank.
  const screeningPrefillReady =
    !getPatientId || patientData?.uuid === getPatientId || Boolean(patientFetchFailed);

  useEffect(() => {
    // JOTFORM-DISCONNECT(BRK-1925): the Reform screening submits through document-service using
    // a minted SCREENING webhook uuid — pullUuid also enforces the one-screening-per-patient-per-day
    // de-dupe. The submit resolves synchronously, so there is no submission event stream.
    if (!document) return;
    dispatch(
      pullUuidAction({
        screeningId: null,
        patientId: getPatientId,
        entityType: 'SCREENING',
        formId: document?.formId,
        isCompleted: false,
      })
    );
  }, [document]);

  return (
    <>
      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={() => backToEOCDetails}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          <Link
            style={{ color: '#111927' }}
            to={'/patients/patient-details/screening'}
          >
            <Typography variant="subtitle1">Back to Screening List</Typography>
          </Link>
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          alignItems="center"
        >
          <Typography variant="h5">Create Screening</Typography>
        </Stack>
      </Stack>

      {screeningPrefillReady && pullUuidResponse.response ? (
        <ScreeningForm
          prefill={screeningPrefill}
          discipline={disciplineString}
          webhookUuid={pullUuidResponse.response?.trim()}
          formId={document?.formId ?? ''}
          onDone={backToScreeningList}
        />
      ) : (
        <img
          src={loader}
          alt="loader"
          style={{ width: '10%', margin: 'auto' }}
        />
      )}
    </>
  );
}

export default ViewAddNewScreenForm;
